import { SignInButton, useClerk } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import {
  ANALYTICS_EVENTS,
  trackAnalyticsEvent,
} from "@cedrum/shared-web/analytics";
import {
  appEntryUrl,
  appSignInUrl,
  appSignUpUrl,
  buildCheckoutIntentUrl,
  isClerkEnabled,
} from '../../config/authUrls.js';
import { resolveCedrumClerkEmailLocale } from '../../ui/clerk/emailLocale.js';

function resolveAuthRedirect(overrideValue) {
  const override = String(overrideValue || '').trim();
  return override || appEntryUrl;
}

function buildSharedClerkAuthProps(forceRedirectUrl = '') {
  const resolvedRedirect = resolveAuthRedirect(forceRedirectUrl);
  return {
    signInUrl: appSignInUrl,
    signUpUrl: appSignUpUrl,
    forceRedirectUrl: resolvedRedirect,
    fallbackRedirectUrl: resolvedRedirect,
  };
}

function trackCta({
  intent = "",
  cta = "",
  location = "site",
} = {}) {
  trackAnalyticsEvent(ANALYTICS_EVENTS.SITE_CTA_CLICKED, {
    intent,
    cta,
    location,
  });

  if (intent === "sign-in" || intent === "sign-up") {
    trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_CTA_CLICKED, {
      intent,
      cta,
      location,
    });
  }

  if (intent === "upgrade") {
    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYWALL_CTA_CLICKED, {
      reason: "marketing_pricing_cta",
      cta,
      location,
    });
  }
}

function buildSignUpModalOptions(forceRedirectUrl = '', locale = '') {
  const sharedClerkAuthProps = buildSharedClerkAuthProps(forceRedirectUrl);
  return {
    signInUrl: sharedClerkAuthProps.signInUrl,
    forceRedirectUrl: sharedClerkAuthProps.forceRedirectUrl,
    fallbackRedirectUrl: sharedClerkAuthProps.fallbackRedirectUrl,
    locale,
  };
}

function ClerkSignUpModalButton({
  className,
  children,
  dataCta = undefined,
  forceRedirectUrl = '',
  intent = 'sign-up',
}) {
  const clerk = useClerk();
  const { i18n } = useTranslation();
  const clerkEmailLocale = resolveCedrumClerkEmailLocale(
    i18n.resolvedLanguage || i18n.language,
  );

  return (
    <button
      type="button"
      className={className}
      data-auth-intent={intent}
      data-auth-cta={dataCta}
      onClick={() => {
        const signUpOptions = buildSignUpModalOptions(
          forceRedirectUrl,
          clerkEmailLocale,
        );
        trackCta({ intent, cta: dataCta });
        clerk.openSignUp(signUpOptions);
      }}
    >
      {children}
    </button>
  );
}

export function SignUpCtaButton({
  className,
  children,
  dataCta = undefined,
  forceRedirectUrl = '',
}) {
  if (!isClerkEnabled) {
    return (
      <a
        className={className}
        href={appSignUpUrl}
        data-auth-intent="sign-up"
        data-auth-cta={dataCta}
        onClick={() => {
          trackCta({ intent: "sign-up", cta: dataCta });
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <ClerkSignUpModalButton
      className={className}
      dataCta={dataCta}
      forceRedirectUrl={forceRedirectUrl}
      intent="sign-up"
    >
      {children}
    </ClerkSignUpModalButton>
  );
}

export function SignInCtaButton({
  className,
  children,
  dataCta = undefined,
  forceRedirectUrl = '',
}) {
  const sharedClerkAuthProps = buildSharedClerkAuthProps(forceRedirectUrl);
  if (!isClerkEnabled) {
    return (
      <a
        className={className}
        href={appSignInUrl}
        data-auth-intent="sign-in"
        data-auth-cta={dataCta}
        onClick={() => {
          trackCta({ intent: "sign-in", cta: dataCta });
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <SignInButton mode="modal" {...sharedClerkAuthProps}>
      <button
        type="button"
        className={className}
        data-auth-intent="sign-in"
        data-auth-cta={dataCta}
        onClick={() => {
          trackCta({ intent: "sign-in", cta: dataCta });
        }}
      >
        {children}
      </button>
    </SignInButton>
  );
}

export function OpenAppCtaLink({ className, children, dataCta = undefined }) {
  return (
    <a
      className={className}
      href={appEntryUrl}
      data-auth-intent="open-app"
      data-auth-cta={dataCta}
      onClick={() => {
        trackCta({ intent: "open-app", cta: dataCta });
      }}
    >
      {children}
    </a>
  );
}

export function UpgradePlanCtaButton({
  className,
  children,
  plan = 'pro',
  dataCta = undefined,
}) {
  const checkoutIntentUrl = buildCheckoutIntentUrl(plan);

  if (!isClerkEnabled) {
    return (
      <a
        className={className}
        href={checkoutIntentUrl}
        data-auth-intent="upgrade"
        data-auth-cta={dataCta}
        onClick={() => {
          trackCta({ intent: "upgrade", cta: dataCta });
        }}
      >
        {children}
      </a>
    );
  }

  const sharedClerkAuthProps = buildSharedClerkAuthProps(checkoutIntentUrl);
  return (
    <ClerkSignUpModalButton
      className={className}
      dataCta={dataCta}
      forceRedirectUrl={sharedClerkAuthProps.forceRedirectUrl}
      intent="upgrade"
    >
      {children}
    </ClerkSignUpModalButton>
  );
}
